import React from 'react';
export function Story() {
  return <div className="max-w-3xl mx-auto px-4 py-4" data-id="element-167">
      <p className="mb-6" data-id="element-168">
        3:17 PM. Client presentation. Simple question about Q4 projections.
      </p>
      <p className="mb-6" data-id="element-169">
        I'd prepared the answer that morning. But standing there, I couldn't
        pull it. My brain felt like concrete. The numbers were just... gone.
      </p>
      <p className="mb-6" data-id="element-170">
        My younger colleague jumped in to save me. I nodded along, chest
        tightening—not from clarity, but from the crushing realization this
        wasn't the first time.
      </p>
      <p className="mb-6" data-id="element-171">
        I was 62. Still running my practice. Still supposed to be the sharp one.
      </p>
      <p className="mb-6" data-id="element-172">But every afternoon, someone unplugged my brain.</p>
      <div className="my-8" data-id="element-173"></div>
      <h2 className="text-xl font-bold mt-10 mb-6" data-id="element-174">
        The Diagnosis That Explained Nothing
      </h2>
      <p className="mb-6" data-id="element-175">
        My doctor flagged it at my physical. Fasting glucose: 112. Pre-diabetic
        range.
      </p>
      <p className="mb-6" data-id="element-176">"Watch your diet. Cut carbs."</p>
      <p className="mb-6" data-id="element-177">
        I did everything right. Skipped breakfast. Avoided snacks. Drank black
        coffee.
      </p>
      <p className="mb-6" data-id="element-178">The crashes kept coming.</p>
      <p className="mb-6" data-id="element-179">
        By 4 PM, I was foggy, irritable, barely able to think. Three months
        later, same thing happens in another meeting. I realize I haven't
        absorbed a word my partner said for five minutes.
      </p>
      <p className="mb-6" data-id="element-180">
        That night, my wife found me staring at my laptop. "Maybe it's time to
        slow down."
      </p>
      <p className="mb-6" data-id="element-181">
        I felt something break. "I'm not ready. I just need to figure out why I
        can't think anymore."
      </p>
      <div className="my-8" data-id="element-182">
        <img src="https://cdn.magicpatterns.com/uploads/k8cYLLczacgfXyPFWvM1dN/gemini-generated-image-rbh4znrbh4znrbh4.jpg" alt="Man working late at desk feeling exhausted" className="w-full h-auto rounded-lg shadow-md" data-id="element-183" />
        <p className="text-center text-sm text-gray-500 mt-2" data-id="element-184">
          The constant fatigue was impacting every aspect of my daily life
        </p>
      </div>
      <h2 className="text-xl font-bold mt-10 mb-6" data-id="element-185">
        The Missing Piece My Doctor Never Mentioned
      </h2>
      <p className="mb-6" data-id="element-186">
        Two weeks later, I ran into Dan—an old colleague—at a conference. He
        looked different. Focused. Energized.
      </p>
      <p className="mb-6" data-id="element-187">
        Over coffee, he told me his story. Same crashes. Same pre-diabetic
        diagnosis. His doctor put him on Metformin—brutal stomach issues,
        constant exhaustion.
      </p>
      <p className="mb-6" data-id="element-188">
        Then his naturopath asked one question that changed everything:
      </p>
      <p className="mb-6" data-id="element-189">"How much stress are you under?"</p>
      <p className="mb-6" data-id="element-190">
        She explained something his doctor had never mentioned:
      </p>
      <p className="mb-6" data-id="element-191">
        "Under chronic stress, your body releases cortisol—your stress hormone.
        Cortisol doesn't just make you feel tense. It directly spikes your blood
        sugar, even if you haven't eaten a single carb."
      </p>
      <p className="mb-6" data-id="element-192">
        My heart rate picked up. "So the client pressure, the deadlines, the
        constant stress—that's literally raising my glucose?"
      </p>
      <p className="mb-6" data-id="element-193">
        "Exactly. Your body thinks you're in an emergency, so it floods your
        bloodstream with glucose for quick energy. Then your insulin tries to
        crash it back down. That's the brain fog. The 3 PM wall. The
        irritability. You're riding a stress-glucose rollercoaster every single
        day, and willpower can't stop it."
      </p>
      <p className="mb-6" data-id="element-194">
        Then she said something that hit me like a freight train:
      </p>
      <p className="mb-6" data-id="element-195">
        "And when you're in your 50s and 60s, your stress response becomes more
        sensitive—meaning the same pressure that barely affected you at 40 now
        spikes your glucose harder than it used to."
      </p>
      <p className="mb-6" data-id="element-196">
        He showed me his glucose logs. Six months ago: wild swings, brutal
        afternoon crashes, numbers all over the map.
      </p>
      <p className="mb-6" data-id="element-197">Now: smooth, steady, consistent.</p>
      <p className="mb-6" data-id="element-198">
        "Your problem isn't just blood sugar. It's your stress response
        hijacking your entire metabolism."
      </p>
      <p className="mb-6" data-id="element-199">
        I felt something click. For the first time in two years, my symptoms
        weren't random. They weren't my fault. They were predictable biology.
      </p>
      <div className="my-8" data-id="element-200">
        <img src="https://cdn.magicpatterns.com/uploads/53N8uU6VG97J4uUNfDdKT8/gemini-generated-image-h1r2mbh1r2mbh1r2.jpg" alt="Professional man working confidently at desk" className="w-full h-auto rounded-lg shadow-md" data-id="element-201" />
        
      </div>
      <h2 className="text-xl font-bold mt-10 mb-6" data-id="element-202">
        Why Everything I'd Tried Had Failed
      </h2>
      <p className="mb-6" data-id="element-203">
        Cutting carbs couldn't stop cortisol from spiking glucose during
        high-pressure meetings.
      </p>
      <p className="mb-6" data-id="element-204">
        More coffee just overloaded an already-stressed system.
      </p>
      <p className="mb-6" data-id="element-205">
        Metformin forced numbers down but did nothing about the stress-cortisol
        loop driving the whole mess.
      </p>
      <p className="mb-6" data-id="element-206">I wasn't broken. My approach was.</p>
      <p className="mb-6" data-id="element-207">"So what did you do?" I asked.</p>
      <p className="mb-6" data-id="element-208">That's when Dan told me about Glucora.</p>
      <p className="mb-6" data-id="element-209">
        Honestly? I almost dismissed it. I'd tried supplements before. Most did
        nothing. But then Dan said something that stopped me:
      </p>
      <p className="mb-6" data-id="element-210">
        "This one isn't trying to force your glucose down. It's designed to
        support the stress response and metabolic signaling your body actually
        needs under pressure."
      </p>
      <div className="my-8" data-id="element-211">
        <img src="https://cdn.magicpatterns.com/uploads/3Q6kHapuxR9KYrBkBbXixU/gemini-generated-image-u2jntvu2jntvu2jn.jpg" alt="Two professionals having coffee meeting" className="w-full h-auto rounded-lg shadow-md" data-id="element-212" />
        
      </div>
    </div>;
}